import { useState, useEffect, useCallback } from 'react'
import ictaApi from '../api/ictaApi'
import {
	APIError,
	Category,
	CreateCategoryPayload,
	CreateCategoryResponse,
	DeleteCategoryResponse,
	GetCategoriesResponse,
	GetCategoryResponse,
	UpdateCategoryPayload,
	UpdateCategoryResponse
} from '../models/category'

// Función para concatenar errores
const parseErrors = (errors?: APIError[]): string | null => {
	if (!errors || errors.length === 0) return null
	return errors[0].msg || 'Error desconocido'
}

export const useCategory = () => {
	const [categories, setCategories] = useState<Category[]>([])
	const [total, setTotal] = useState<number>(0)
	const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)

	const [loadingList, setLoadingList] = useState<boolean>(false)
	const [loadingItem, setLoadingItem] = useState<boolean>(false)
	const [creating, setCreating] = useState<boolean>(false)
	const [updating, setUpdating] = useState<boolean>(false)
	const [deleting, setDeleting] = useState<boolean>(false)

	const [error, setError] = useState<string | null>(null)

	// Obtener lista
	const fetchCategories = useCallback(async () => {
		setLoadingList(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetCategoriesResponse>('/category')
			const errMsg = parseErrors(data.errors)
			if (errMsg) setError(errMsg)
			else {
				setCategories(data.categories)
				setTotal(data.total)
			}
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener categorías')
		} finally {
			setLoadingList(false)
		}
	}, [])

	// Obtener una categoría
	const fetchCategory = useCallback(async (id: string) => {
		setLoadingItem(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetCategoryResponse>(`/category/${id}`)
			const errMsg = parseErrors(data.errors)
			if (errMsg) setError(errMsg)
			else if (data.category) setSelectedCategory(data.category)
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener categoría')
		} finally {
			setLoadingItem(false)
		}
	}, [])

	// Crear categoría
	const createCategory = useCallback(async (payload: CreateCategoryPayload) => {
		setCreating(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<CreateCategoryResponse>('/category', payload)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
				return null
			}
			if (data.category) setCategories((prev) => [data.category!, ...prev])
			return data.category!
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al crear categoría')
			return null
		} finally {
			setCreating(false)
		}
	}, [])

	// Actualizar categoría
	const updateCategory = useCallback(
		async (id: string, payload: UpdateCategoryPayload) => {
			setUpdating(true)
			setError(null)
			try {
				const { data } = await ictaApi.put<UpdateCategoryResponse>(`/category/${id}`, payload)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				if (data.category) {
					setCategories((prev) => prev.map((c) => (c.uid === id ? data.category! : c)))
					if (selectedCategory?.uid === id) setSelectedCategory(data.category)
					return data.category!
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al actualizar categoría')
				return null
			} finally {
				setUpdating(false)
			}
		},
		[selectedCategory]
	)

	// Eliminar categoría
	const deleteCategory = useCallback(async (id: string) => {
		setDeleting(true)
		setError(null)
		try {
			const { data } = await ictaApi.delete<DeleteCategoryResponse>(`/category/${id}`)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
				return false
			}
			setCategories((prev) => prev.filter((c) => c.uid !== id))
			return true
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al eliminar categoría')
			return false
		} finally {
			setDeleting(false)
		}
	}, [])

	useEffect(() => {
		fetchCategories()
	}, [fetchCategories])

	// Alert de error
	useEffect(() => {
		if (error) {
			alert(`Error: ${error}`)
			setError(null) // Reset error after showing alert
		}
	}, [error])

	return {
		categories,
		total,
		selectedCategory,
		loadingList,
		loadingItem,
		creating,
		updating,
		deleting,
		error,
		fetchCategories,
		fetchCategory,
		createCategory,
		updateCategory,
		deleteCategory
	}
}
